import type { CollectionWinner } from './winners';

/*
 * CSV of the collection winners, in the format the client supplied as the
 * example to match: three columns under their raw database names, and nothing
 * else. Extra columns were removed on 2026-09-15 — a file that has to line up
 * with an existing sheet is not improved by additions.
 */

const COLUMNS: Array<{ key: string; of: (r: CollectionWinner) => string }> = [
  { key: 'prize_name', of: (r) => r.prize ?? '' },
  { key: 'redeemed_at', of: (r) => fmtStamp(r.date) },
  // The card number, which is also the member id — `medium_code` is the name
  // the client's own export uses, so it is the name the header carries.
  { key: 'medium_code', of: (r) => r.cardNumber ?? r.memberId ?? '' },
];

/**
 * ISO → "2026-09-11 10:04:55.673" in Paris, matching the example file down to
 * the fractional second: two completions in the same second are ordinary here,
 * and a stamp truncated to the second makes them look simultaneous.
 */
export function fmtStamp(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const ms = String(d.getUTCMilliseconds()).padStart(3, '0');
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}.${ms}`;
}

/**
 * One CSV field.
 *
 * Quoted whenever it contains a comma, a semicolon, a quote or a newline. A field that
 * OPENS with =, +, - or @ is also prefixed with a quote character: spreadsheets
 * treat those as formulas, and a card number or name should never be executed
 * by the thing that opens the file.
 */
export function csvField(value: string): string {
  const risky = /^[=+\-@\t\r]/.test(value);
  const body = risky ? `'${value}` : value;
  return /[",;\n\r]/.test(body) ? `"${body.replace(/"/g, '""')}"` : body;
}

/**
 * The file's bytes.
 *
 * COMMA-separated, as the example file is — it reads like a database export and
 * that is what it has to stay compatible with. The UTF-8 BOM is kept so Excel
 * renders "Séjour" rather than "SÃ©jour"; note that on French Windows, where the
 * list separator is `;`, a comma file opens in one column unless it is imported
 * rather than double-clicked. That is the cost of matching the example exactly.
 */
export function buildWinnersCsv(rows: CollectionWinner[]): string {
  const lines = [COLUMNS.map((c) => csvField(c.key)).join(',')];
  for (const r of rows) lines.push(COLUMNS.map((c) => csvField(c.of(r))).join(','));
  const BOM = '\uFEFF';
  return `${BOM}${lines.join('\r\n')}\r\n`;
}

/** `gagnants-collections_2026-09-08_2026-09-15.csv` — the filters, in the name. */
export function csvFileName(from: string | null, to: string | null): string {
  const span = [from, to].filter(Boolean).join('_');
  return `gagnants-collections${span ? `_${span}` : ''}.csv`;
}

/** Hand the file to the browser. Split out so the builder above stays testable. */
export function downloadCsv(name: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
